import { Trans } from '@lingui/react/macro';
import { Copy01 } from '@untitledui/icons/Copy01';
import { Mail01 } from '@untitledui/icons/Mail01';

import { Button } from '@/components/base/buttons/button';
import { MessengerIcon } from '@/components/foundations/messenger-icon';
import { WhatsAppIcon } from '@/components/foundations/whatsapp-icon';
import {
  NETWORK_REFERRAL_MINIMUM_INCENTIVE,
  NETWORK_REFERRAL_REWARD,
} from '@/constants/config';
import { useClipboard } from '@/hooks/use-clipboard';
import { useAnalytics } from '@/lib/analytics/use-analytics';

interface ReferralBannerProps {
  referralLink?: string;
  messengerLink?: string;
}

export function ReferralBanner({
  referralLink,
  messengerLink,
}: ReferralBannerProps) {
  const { copy, copied } = useClipboard();
  const analytics = useAnalytics();

  const handleCopyLink = () => {
    if (referralLink) {
      analytics?.ui({
        action: 'clicked',
        actionSubject: 'referralCopyLink',
        source: 'projectPage',
        attributes: { channel: 'copy_link' },
      });
      void copy(referralLink);
    }
  };

  const handleEmailShare = () => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'referralShare',
      actionSubjectId: 'email',
      source: 'projectPage',
      attributes: { channel: 'email' },
    });
    if (referralLink) {
      const subject = encodeURIComponent(
        'Join Respondent and earn money for your opinions!',
      );
      const body = encodeURIComponent(
        `I thought you might be interested in joining Respondent to participate in paid research studies. Sign up using my referral link: ${referralLink}`,
      );
      window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    }
  };

  const handleMessengerShare = () => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'referralShare',
      actionSubjectId: 'messenger',
      source: 'projectPage',
      attributes: { channel: 'messenger' },
    });
    if (messengerLink) {
      window.open(messengerLink, '_blank');
    }
  };

  const handleWhatsAppShare = () => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'referralShare',
      actionSubjectId: 'whatsapp',
      source: 'projectPage',
      attributes: { channel: 'whatsapp' },
    });
    if (referralLink) {
      const text = encodeURIComponent(
        `Join Respondent and earn money for your opinions! Sign up here: ${referralLink}`,
      );
      window.open(`https://wa.me/?text=${text}`, '_blank');
    }
  };

  return (
    <div className="rounded-2xl bg-utility-blue-light-100 px-6 py-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        {/* Emoji */}
        <div className="shrink-0 text-display-md">📣</div>

        {/* Text Content */}
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-lg font-semibold text-primary">
            <Trans>Share this project</Trans>
          </h3>
          <p className="text-md text-secondary">
            <Trans>
              Earn{' '}
              <span className="font-semibold text-primary">
                ${NETWORK_REFERRAL_REWARD}
              </span>{' '}
              when you refer a new Respondent user who earns{' '}
              <span className="font-semibold text-primary">
                ${NETWORK_REFERRAL_MINIMUM_INCENTIVE}
              </span>{' '}
              in total incentive.
            </Trans>
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex shrink-0 items-center gap-3">
          <Button
            color="secondary"
            size="md"
            iconLeading={Copy01}
            onClick={handleCopyLink}
          >
            {copied ? <Trans>Copied!</Trans> : <Trans>Copy link</Trans>}
          </Button>

          <Button
            color="secondary"
            size="md"
            iconLeading={Mail01}
            onClick={handleEmailShare}
            aria-label="Share via email"
          />

          <Button
            color="secondary"
            size="md"
            onClick={handleMessengerShare}
            aria-label="Share via Messenger"
            iconLeading={MessengerIcon}
          />

          <Button
            color="secondary"
            size="md"
            onClick={handleWhatsAppShare}
            aria-label="Share via WhatsApp"
            iconLeading={WhatsAppIcon}
          />
        </div>
      </div>
    </div>
  );
}
